import {
  FrontendRegistration,
  RegistrationActivity,
  FrontendRegistrationCourse,
  FrontendRegistrationGroup,
} from "@timeedit/registration-schemas";
import { Mapping, isDefined } from "@timeedit/registration-shared";
import { Column, EditableTable as Table } from "@timeedit/ui-components";
import { ExpandableConfig } from "@timeedit/ui-components/antd/table/interface";
import dayjs from "dayjs";
import { ComponentProps, ComponentType } from "react";
import {
  NavigateAction,
  TitleOptions as RBCTitleOptions,
  ViewProps,
  ViewStatic,
} from "react-big-calendar";
import { CalendarEvent } from "../../../../utils/events";
import { LoadActions, RegistrationRequiredTranslations } from "../../types";
import { ExpandableComponent } from "./ListViewColumns";
import { TimeColumn } from "./TimeColumn";

type ListViewProps = {
  events: CalendarEvent[];
  registration: FrontendRegistration;
  mapping: Mapping;
  translations: RegistrationRequiredTranslations;
  loadActions: LoadActions;
};
export function ListView({
  events = [],
  registration,
  mapping,
  translations,
  loadActions,
}: ListViewProps): React.JSX.Element {
  const dataSource = createDataSource({
    events,
    registration,
  });

  return (
    <div>
      {createTable({
        size: "middle",
        tableLayout: "auto",
        dataSource,
        showHeader: false,
        columns: createColumns(translations, mapping, registration),
        pagination: false,
        expandable: tableExpandable({
          mapping,
          registration,
          translations,
          loadActions,
        }),
      })}
    </div>
  );
}

type ScheduleListViewProps = ViewProps<CalendarEvent> & ListViewProps;

function ScheduleListView(props: ScheduleListViewProps): React.JSX.Element {
  return <ListView {...props} />;
}
interface CreateTableProps<T extends object>
  extends Omit<ComponentProps<typeof Table<T>>, "dataSource" | "columns"> {
  dataSource: T[];
  columns: Column<T>[];
}

export type ScheduleListDataSource = {
  course: FrontendRegistrationCourse;
  activity: RegistrationActivity;
  group: FrontendRegistrationGroup;
  event: CalendarEvent;
  key: string;
};

function createTable({
  columns,
  dataSource,
  ...props
}: CreateTableProps<ScheduleListDataSource>): React.JSX.Element {
  return <Table {...props} columns={columns} dataSource={dataSource} />;
}

interface CreateDataSource {
  registration: FrontendRegistration;
  events: CalendarEvent[];
}

function createDataSource({
  registration,
  events,
}: CreateDataSource): ScheduleListDataSource[] {
  return events.reduce((dataSource: ScheduleListDataSource[], event, index) => {
    const group = registration.groups[event.data.groupId];
    if (!isDefined(group)) return dataSource;

    const activity = registration.activities[group.parentId];
    if (!isDefined(activity)) return dataSource;
    const course = registration.courses[activity.parentId];
    if (!isDefined(course)) return dataSource;

    dataSource.push({
      activity,
      group,
      course,
      event,
      key: `${group.id.toString()} - ${index}`,
    });
    return dataSource;
  }, []);
}
type TableExpandableProps = {
  registration: FrontendRegistration;
  mapping: Mapping;
  translations: RegistrationRequiredTranslations;
  loadActions: LoadActions;
};
function tableExpandable(
  props: TableExpandableProps
): ExpandableConfig<ScheduleListDataSource> {
  return {
    expandedRowRender: ({ event }: ScheduleListDataSource) => {
      return <ExpandableComponent {...props} event={event} />;
    },
  };
}

ScheduleListView.navigate = (
  date: Date,
  action: NavigateAction,
  props: unknown
): Date => {
  const parameters = { date, action, props };
  return parameters.date;
};

interface TitleOptions extends Partial<RBCTitleOptions> {
  localizer: ViewProps["localizer"];
  length: ViewProps["length"];
}

ScheduleListView.title = (
  date: Date,
  { localizer, length }: TitleOptions
): string => {
  const end = localizer.add(date, length, "day");
  return localizer.format({ date, end }, "agendaHeaderFormat");
};

function createColumns(
  translations: RegistrationRequiredTranslations,
  mapping: Mapping,
  registration: FrontendRegistration
): Column<ScheduleListDataSource>[] {
  const columns: Column<ScheduleListDataSource>[] = [
    {
      title: translations.COURSE,
      dataIndex: "course",
      key: "course",
      render: (_, { course }) => {
        const label = mapping.parse("courseLabel", course.teObject);
        const title = mapping.parse("courseTitle", course.teObject);
        return (
          <div>
            {title} - {label}
          </div>
        );
      },
    },
    {
      title: translations.time,
      dataIndex: "time",
      key: "time",
      render: (_, { event }) => (
        <TimeColumn events={[event]} timezone={registration.timezone} />
      ),
      sorter: {
        multiple: 1,
        compare: (a, b) =>
          dayjs(a.event.start).hour() - dayjs(b.event.start).hour(),
      },
    },
  ];
  return columns;
}

// eslint-disable-next-line import/no-unused-modules
export default ScheduleListView as ComponentType<ViewProps<CalendarEvent>> &
  ViewStatic;
